import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HomeService } from '../../services/home.service';
import { LanguageService } from 'src/app/services/language.service';
import { environment } from 'src/environments/environment';
import { MainImages } from '../../models/main-images';
import { LanguageEnum } from 'src/app/shared/models/item.model';
import { Status } from 'src/app/@core/enums/status.enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CarouselResize } from 'src/app/shared/models/carousel-resize';
import { CustomCarouselComponent } from 'src/app/shared/components/custom-carousel/custom-carousel.component';
import { IdentityService } from 'src/app/@core/services/identity.service';
import { UNIFIED_CODE_TOKENS } from 'src/app/@core/design/unified-code-tokens';
import { HeroCta, resolveHeroCta } from '../../services/hero-cta';

@Component({
  selector: 'app-main-slider',
  templateUrl: './main-slider.component.html',
  styleUrls: ['./main-slider.component.scss']
})
export class MainSliderComponent implements OnInit, OnDestroy {
  isRtl: boolean = true;
  lastSubTitle: string = "";
  private destroySubject: Subject<void> = new Subject();
  homeImageResource: string = environment.homeImageResource;
  carouselResize: CarouselResize<MainImages> = {
    data: [],
    items: [],
    itemSizeSm: 1,
    itemSizeMd: 1,
    itemSizeLg: 1,
    itemResize: 0,
    innerWidth: 0,
    currentIndex: 0,
    slideDelay: 15000
  }
  @ViewChild('appCustomCarousel') appCustomCarousel: CustomCarouselComponent<MainImages>;

  constructor(private _HomeService: HomeService, private _LanguageService: LanguageService, private _IdentityService: IdentityService){};

  ngOnInit(): void {
    this.isRtl = this._LanguageService.getCurrentLanguage() === LanguageEnum.ar;
    this._LanguageService.languageChange
    .pipe(takeUntil(this.destroySubject))
    .subscribe((result: LanguageEnum) => this.isRtl = result === LanguageEnum.ar);
    this.getSliderData();
  }

  getSliderData(): void {
    this._HomeService.getSliderData()
    .pipe(takeUntil(this.destroySubject))
    .subscribe((result: MainImages[]) => {
      this.carouselResize.data = result.filter(c => c.status === Status.Active);
      if (!this.isUnified) return;
      // Autoplay is off by default for users who ask for reduced motion; the play button still starts it.
      this.unifiedPaused = this.prefersReducedMotion();
      this.startUnifiedAutoplay();
    });
  }

  // ---- Unified Code hero (criterion 7 / 38): own accessible carousel, the Custom one is untouched.
  unifiedIndex: number = 0;
  unifiedPaused: boolean = false;
  private unifiedTimer: ReturnType<typeof setInterval> | null = null;

  get isUnified(): boolean {
    return this._IdentityService.isUnifiedCode();
  }

  get unifiedSlides(): MainImages[] {
    return this.carouselResize.data;
  }

  heroCta(item: MainImages): HeroCta {
    return resolveHeroCta(item);
  }

  getImageUrl(item: MainImages): string {
    return item.bannerType === 0 ? item.image : this.homeImageResource + item.image;
  }

  goToSlide(index: number): void {
    const count = this.unifiedSlides.length;
    if (!count) return;
    this.unifiedIndex = (index + count) % count;
  }

  toggleUnifiedAutoplay(): void {
    this.unifiedPaused = !this.unifiedPaused;
    if (this.unifiedPaused) this.stopUnifiedAutoplay();
    else this.startUnifiedAutoplay();
  }

  private startUnifiedAutoplay(): void {
    this.stopUnifiedAutoplay();
    if (this.unifiedPaused || this.unifiedSlides.length < 2) return;
    this.unifiedTimer = setInterval(() => this.goToSlide(this.unifiedIndex + 1), this.carouselResize.slideDelay);
  }

  private stopUnifiedAutoplay(): void {
    if (this.unifiedTimer) clearInterval(this.unifiedTimer);
    this.unifiedTimer = null;
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  getTextColor(item: MainImages): string {
    return this._IdentityService.isUnifiedCode() ? UNIFIED_CODE_TOKENS.textOnColor : item.textColor;
  }

  getSubTitle(subTitle: string): string {
    if(!subTitle) return;
    
    let currentSubTitle = subTitle.split(" ");
    
    if(currentSubTitle.length > 1){
      this.lastSubTitle = currentSubTitle.slice(-1).join(" ");
      return currentSubTitle.slice(0, -1).join(" ");
    } else {
      this.lastSubTitle = "";
      return subTitle;
    }
  }

  ngOnDestroy(): void {
    this.stopUnifiedAutoplay();
    this.destroySubject.next();
  }
}
